import { createContext, useContext, useEffect, useState } from "react";
import { LoginService } from "../modules/login/services";
import { Permissions, TUser } from "../modules/users/models";
import permissionsStore from "../store/permissions";
import { useNavigationContext } from "./navigation-context";

interface IAuthContextData {
  login: (
    email: string,
    password: string,
    token: string
  ) => Promise<string | void>;
  logout: () => void;
  isAmbassadorContext: boolean;
  setIsAmbassadorContext: (ambassador: boolean) => void;
  user: TUser | null;
  loading: boolean;
  loginEmailConfirmation: (res: any) => Promise<void>;
  proceedLogin: (user: TUser) => Promise<void>;
}

const AuthContext = createContext<IAuthContextData>({
  login: async () => {},
  logout: () => {},
  isAmbassadorContext: false,
  setIsAmbassadorContext: async () => {},
  user: null,
  loading: false,
  loginEmailConfirmation: async () => {},
  proceedLogin: async () => {}
});

interface IAuthProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<IAuthProps> = ({ children }) => {
  // const { socket } = useContext(SocketContext); Adicionar socket para persistir a sessão?
  const { setSelectedAccountCombo } = useNavigationContext();
  const [user, setUser] = useState<TUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isAmbassadorContext, setIsAmbassadorContext] =
    useState<boolean>(false);

  const {
    setPermissions,
    clearPermissions,
    setRole,
    setScenarioId,
    setStorageUser,
    clearAll
  } = permissionsStore();

  useEffect(() => {
    console.debug("Started Puxa-Capivara");
    getCurrentUser();
  }, []);

  const handleLogin = async (
    email: string,
    password: string,
    token: string
  ): Promise<string | void> => {
    const loginService = await LoginService.login(email, password, token);

    if (loginService) {
      if (!loginService?.status) {
        return loginService.message;
      }
      proceedLogin(loginService.user);
    }
  };

  const proceedLogin = async (user: TUser) => {
    setScenarioId(
      user.accounts.map((account) => {
        return {
          accountId: account.id,
          scenarioId: account.currentScenarioId
        };
      })
    );
    parseUserPermission(user);
    setStorageUser(user);
    const oldestAccount = user.accounts.reduce((oldest, account) => {
      return new Date(account.createdAt) < new Date(oldest.createdAt)
        ? account
        : oldest;
    });
    setSelectedAccountCombo(oldestAccount.id);
    getCurrentUser();
  };

  const handleLogout = async () => {
    setLoading(true);
    const clearCookies = () => {
      const cookies = document.cookie.split("; ");
      cookies.forEach((cookie) => {
        const [name] = cookie.split("=");
        if (name !== "cookie-consent") {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        }
      });
    };

    const clearStorage = () => {
      localStorage.clear();
      sessionStorage.clear();
    };
    await LoginService.logout()
      .then(() => {
        clearCookies();
        clearStorage();
        setUser(null);
        clearAll();
        clearPermissions();
        setRole(undefined);
        setSelectedAccountCombo(null);
      })
      .finally(() => {
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const getCurrentUser = async () => {
    try {
      const fetchedUser = await LoginService.currentUser();
      setUser(fetchedUser);
    } finally {
      setLoading(false);
    }
  };

  const parseUserPermission = async (user: TUser) => {
    const { role } = user;

    setRole(role);

    let permissions: Permissions[] = [];

    switch (role) {
      case "owner":
        permissions = [
          Permissions.CREDITS,
          Permissions.ANALYSIS,
          Permissions.HISTORY,
          Permissions.SCENARIOS,
          Permissions.ACCOUNTS,
          Permissions.USERS,
          Permissions.CONFIGURATIONS,
          Permissions.AMBASSADOR
        ];
        break;
      case "master":
        permissions = [
          Permissions.CREDITS,
          Permissions.ANALYSIS,
          Permissions.HISTORY,
          Permissions.SCENARIOS,
          Permissions.ACCOUNTS,
          Permissions.USERS,
          Permissions.CONFIGURATIONS,
          Permissions.BI,
          Permissions.AMBASSADOR
        ];
        break;
      case "admin":
        permissions = [
          Permissions.CREDITS,
          Permissions.ANALYSIS,
          Permissions.HISTORY,
          Permissions.SCENARIOS,
          Permissions.USERS,
          Permissions.CONFIGURATIONS,
          Permissions.AMBASSADOR
        ];
        break;
      case "collaborator":
        permissions = [
          Permissions.ANALYSIS,
          Permissions.HISTORY,
          Permissions.SCENARIOS,
          Permissions.CONFIGURATIONS
        ];
        break;
      case "support":
        permissions = [
          Permissions.CREDITS,
          Permissions.ANALYSIS,
          Permissions.HISTORY,
          Permissions.SCENARIOS,
          Permissions.CONFIGURATIONS,
          Permissions.SUPPORT,
          Permissions.AMBASSADOR
        ];

        break;
      default:
        break;
    }
    setPermissions(permissions);
  };

  const loginEmailConfirmation = async (res: any) => {
    if (res && res.success) {
      setScenarioId(
        res.user.accounts.map((account: any) => {
          return {
            accountId: account.id,
            scenarioId: account.currentScenarioId
          };
        })
      );
      await parseUserPermission(res.user);
      setStorageUser(res.user);
      setUser(res.user);
    }
    setLoading(false);
  };

  return (
    <AuthContext.Provider
      value={{
        isAmbassadorContext,
        setIsAmbassadorContext,
        login: handleLogin,
        logout: handleLogout,
        user,
        loading,
        loginEmailConfirmation,
        proceedLogin
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
// eslint-disable-next-line
export const useAuthContext = () => useContext(AuthContext);
